import {Injectable} from '@angular/core';
import {AbstractControl} from '@angular/forms';

import {BehaviorSubject, Observable} from 'rxjs';
import {debounceTime, startWith, tap} from 'rxjs/operators';

import {EmpZoneDependency, GroupZoneDescriptor} from './form-generator.interfaces';
import {FormGeneratorUtils} from './form-generator.utils';

import {groupBy} from '../utils/arrays';

@Injectable({
  providedIn: 'root'
})
export class EmpZoneDependencyUtils {

  private static readonly RESET_ACTION_VALUE = '__reset';
  private static readonly TRIGGER_NOT_SET = 'NOT_SET';
  private static readonly TRIGGER_IS_SET = 'IS_SET';

  public static isReset(dependency: EmpZoneDependency): boolean {
    return dependency.actionValue === this.RESET_ACTION_VALUE;
  }

  public static reset(dependency: EmpZoneDependency): EmpZoneDependency {
    return {
      source: dependency.source,
      target: dependency.target,
      trigger: dependency.trigger,
      action: dependency.action,
      actionValue: this.RESET_ACTION_VALUE
    };
  }

  public registerDependenciesOnOneGroup(descriptor: GroupZoneDescriptor,
                                        formGroup: AbstractControl,
                                        triggerAccessor: (target: string) =>
                                          BehaviorSubject<EmpZoneDependency>[]): Array<Observable<any>> {
    return this.registerDependencies(descriptor,
      name => formGroup?.get(name),
      value => value,
      triggerAccessor);
  }

  public registerDependencies(descriptor: GroupZoneDescriptor,
                              controlAccessor: (name: string) => AbstractControl,
                              valueAccessor: (controlValue: any) => any,
                              triggerAccessor: (target: string) => BehaviorSubject<EmpZoneDependency>[]): Array<Observable<any>> {
    const dependencies = descriptor.dependencies;
    if (!dependencies || dependencies.length <= 0) {
      return [];
    }

    // group dependencies by source and target
    const dependenciesBySourceTarget: { [key: string]: Array<EmpZoneDependency> } = groupBy(
      dependencies,
      (dependency: EmpZoneDependency) => dependency.source + dependency.target
    );

    // for each group source/target dependencies
    return Object.values(dependenciesBySourceTarget)
      .map(dependenciesOfSourceTarget => {
        const sourceControl = controlAccessor(FormGeneratorUtils
          .simplifyZoneName(FormGeneratorUtils
            .sanitizeZoneName(dependenciesOfSourceTarget[0].source)));
        return sourceControl?.valueChanges
          .pipe(
            debounceTime(100),
            // to trigger on initial value
            startWith(sourceControl?.value),
            tap((controlValue) => {
              const value = valueAccessor(controlValue);
              // on source value changed
              const toTrigger = dependenciesOfSourceTarget
                .filter(dependency => this.isTriggered(value, dependency));
              const toReset = dependenciesOfSourceTarget
                .filter(dependency => toTrigger.indexOf(dependency) === -1);

              const triggers = triggerAccessor(dependenciesOfSourceTarget[0].target);

              // first reset all non-matched trigger dependencies
              toReset.forEach(dependency => triggers
                ?.forEach(trigger => trigger
                  .next(EmpZoneDependencyUtils.reset(dependency))));

              // then trigger matched trigger dependencies
              toTrigger.forEach(dependency => triggers
                ?.forEach(trigger => trigger
                  .next(dependency)));
            })
          );
      })
      .filter(dependencyObservable => !!dependencyObservable);
  }

  private isTriggered(value: any, dependency: EmpZoneDependency): boolean {
    const isEmpty = this.normalizeComparableValues(value).length <= 0;
    if ((isEmpty && dependency.trigger === EmpZoneDependencyUtils.TRIGGER_NOT_SET)
      || (!isEmpty && dependency.trigger === EmpZoneDependencyUtils.TRIGGER_IS_SET)) {
      return true;
    }

    const listTrigger = this.parseListTrigger(dependency.trigger);
    if (listTrigger) {
      return listTrigger.negated
        ? this.isTriggeredByNotIn(value, listTrigger.values)
        : this.isTriggeredByIn(value, listTrigger.values);
    }

    // legacy
    return (value + '') === dependency.trigger;
  }

  private parseListTrigger(trigger: string): { negated: boolean; values: string[] } | null {
    const normalizedTrigger = trigger?.trim();
    if (!normalizedTrigger || !normalizedTrigger.endsWith(']')) {
      return null;
    }

    const openingIndex = normalizedTrigger.indexOf('[');
    if (openingIndex < 0) {
      return null;
    }

    const prefix = normalizedTrigger
      .substring(0, openingIndex)
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/_/g, ' ')
      .toUpperCase();

    const isPositiveListTrigger = prefix === '' || prefix === 'IN';
    const isNegatedListTrigger = prefix === '!' || prefix === 'NOT IN';
    if (!isPositiveListTrigger && !isNegatedListTrigger) {
      return null;
    }

    return {
      negated: isNegatedListTrigger,
      values: this.parseTriggerValues(normalizedTrigger, openingIndex + 1)
    };
  }

  private parseTriggerValues(trigger: string, startIndex: number): string[] {
    return trigger
      .substring(startIndex, trigger.length - 1)
      .split(',')
      .map(value => value.trim())
      .filter(value => value.length > 0);
  }

  private normalizeComparableValues(value: any): string[] {
    if (Array.isArray(value)) {
      return value
        .map(item => this.normalizeComparableValue(item))
        .filter(item => item.length > 0);
    }

    const normalizedValue = this.normalizeComparableValue(value);
    if (!normalizedValue) {
      return [];
    }

    return [normalizedValue];
  }

  private normalizeComparableValue(value: any): string {
    return value === null || value === undefined ? '' : (value + '').trim();
  }

  private isTriggeredByIn(value: any, triggerValues: string[]): boolean {
    const comparableValues = this.normalizeComparableValues(value);
    if (comparableValues.length <= 0) {
      return false;
    }
    return comparableValues.some(comparableValue => triggerValues.includes(comparableValue));
  }

  private isTriggeredByNotIn(value: any, triggerValues: string[]): boolean {
    const comparableValues = this.normalizeComparableValues(value);
    if (comparableValues.length <= 0) {
      return true;
    }
    return comparableValues.every(comparableValue => !triggerValues.includes(comparableValue));
  }
}
